/**
 * App Component - Root Application Component
 *
 * Auth.HireNow.com - Central Authentication SPA
 *
 * This is the root component that sets up:
 * - React Router for navigation
 * - Authentication Provider for auth state
 * - Route definitions for the application
 *
 * Flow:
 * 1. User lands on / (LoginPage)
 * 2. User clicks "Sign in with Microsoft"
 * 3. MSAL handles authentication
 * 4. On success, user is redirected to /redirect
 * 5. AuthRedirectHandler reads roles and redirects to downstream app
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { LoginPage, Loading } from './components';
import { AuthRedirectHandler } from './routing';

/**
 * AppRoutes Component
 *
 * Defines the application routes with authentication-aware navigation.
 * Uses the auth context to determine which routes to show.
 */
function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen during initial auth check
  if (isLoading) {
    return <Loading fullScreen message="Initializing..." />;
  }

  return (
    <Routes>
      {/* Login Page - shown when not authenticated */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            // Already authenticated, redirect to handler
            <Navigate to="/redirect" replace />
          ) : (
            <LoginPage />
          )
        }
      />

      {/* Auth Redirect Handler - processes post-login redirection */}
      <Route
        path="/redirect"
        element={
          isAuthenticated ? (
            <AuthRedirectHandler />
          ) : (
            // Not authenticated, go back to login
            <Navigate to="/" replace />
          )
        }
      />

      {/* Callback route for MSAL redirect flow */}
      <Route
        path="/auth/callback"
        element={
          // MSAL will handle the callback automatically via handleRedirectPromise
          // This route just needs to exist for the redirect URI
          <Loading fullScreen message="Completing sign-in..." />
        }
      />

      {/* Logout confirmation page */}
      <Route
        path="/logout"
        element={
          <div className="logout-page">
            <h1>Signed Out</h1>
            <p>You have been successfully signed out.</p>
            <a href="/" className="btn btn-primary">
              Sign in again
            </a>
          </div>
        }
      />

      {/* 404 - Catch all unmatched routes */}
      <Route
        path="*"
        element={
          <div className="not-found-page">
            <h1>Page Not Found</h1>
            <p>The page you're looking for doesn't exist.</p>
            <a href="/" className="btn btn-primary">
              Go to Login
            </a>
          </div>
        }
      />
    </Routes>
  );
}

/**
 * App Component
 *
 * The root component that wraps the application with necessary providers.
 */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app">
          <AppRoutes />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
